import {CommonDialogs, CommonTooltips, YesNoDialogProps} from "@mcleod/common";
import {
    BlurEvent,
    ChangeEvent,
    ClickEvent,
    DropdownItem,
    DropdownSelectionEvent,
    EditRowDecorator,
    Layout,
    SaveButton,
    Snackbar,
    Switch,
    TableContentsChangedEvent,
    TableRow,
    TableRowDisplayEvent,
    TableRowModeChangeEvent,
    Textbox
} from "@mcleod/components";
import {Alignment, Api, LogManager, ModelRow, StringUtil} from "@mcleod/core";
import {AutogenLayoutDistanceSource} from "./autogen/AutogenLayoutDistanceSource";
import {DistancePreferences} from "./DistancePreferences";
import {
    DistanceOptions,
    INTELLIROUTE,
    INTELLIROUTE_LOOKUP_METHODS,
    INTELLIROUTE_LOOKUP_TYPES,
    MILEMAKER,
    MILEMAKER_GUIDE19_METHODS,
    MILEMAKER_GUIDE20_METHODS,
    MILEMAKER_LOOKUP_TYPES,
    PCMILER_ON_PREM,
    PCMILER_ON_PREM_LOOKUP_METHODS,
    PCMILER_WEB,
    PCMILER_WEB_LOOKUP_METHODS,
    PCMILER_LOOKUP_TYPES
} from "./DistanceOptions";

const log = LogManager.getLogger("lme.powerbroker.DistanceSourcePcMiler");
const pcMiler = { web: "T", onPrem: "A"};
const mileMaker = { web: "E", intelliroute: "R"};
const promiles = {web: "P"}

const LATEST_PCMILER_VERSION = 38;
const OLDEST_PCMILER_VERSION = 18;

export class DistanceSource extends AutogenLayoutDistanceSource {
    private PCMILER_WEB_URL = "https://pcmiler.alk.com/apis/rest/v1.0/Service.svc";

    versions: string[] = [];
    private _sourceId: String;

    get sourceId(): String {
        return this._sourceId;
    }

    set sourceId(value: String) {
        this._sourceId = value;
    }

    onLoad() {
        this.hideServiceProvider();
        this.setDistanceOptionsPanel();
    }

    async getAvailableDistanceSourceVersions(provider: string) {
        this.setTextboxVersions();
        const versionIds = await this.getVersionIds(provider);
        this.versions = [];
        for (let i = 0; i < versionIds?.length; i++) {
            this.versions.push(versionIds[i]);
        }

        this.updateTableDistanceProfileQuickAddVersionItems();
        this.updateTableDistanceProfileVersionItems();
    }

    async getVersionIds(provider: string) {
        const response = await Api.search("lme/dispatch/distance/distance-source-versions", { source: provider });
        const versionIds = response?.data[0]?.version_id ?? [];

        // Sort versionIds in descending order (assuming numeric or string values)
        if (Array.isArray(versionIds)) {
            versionIds.sort((a, b) => {
                // Try to compare as numbers, fallback to string comparison
                const numA = Number(a), numB = Number(b);
                if (!isNaN(numA) && !isNaN(numB)) {
                    return numB - numA;
                }
                return b.localeCompare(a);
            });
        }
        return versionIds;
    }

    async postDataSources(): Promise<boolean> {
        try {
            if (this.mainDataSource?.hasChanged()) {
                await this.mainDataSource.post();
            }

            if (this.sourceDispatchControl?.hasChanged()) {
                await this.sourceDispatchControl.post();
            }

            if (this.sourceDistanceSource?.hasChanged()) {
                await this.sourceDistanceSource.post();
            }
            return true;
        } catch (error) {
            log.error(error);
            return false;
        }
    }

    async textboxDistanceVendorOptionsOnChange(event: ChangeEvent) {
        const selectedItem = (event.target as Textbox).selectedItem;
        if (selectedItem) {
            const dropdownValue = selectedItem.value;
            this.sourceId = dropdownValue;
            await this.getAvailableDistanceSourceVersions(dropdownValue);
            const isPCMilerWebService = dropdownValue === pcMiler.web;
            const isMileMakerWeb = dropdownValue === mileMaker.web;
            const isProMiles: boolean = dropdownValue === promiles.web;
            this.textboxWebserviceUrl.visible = isPCMilerWebService || isMileMakerWeb;
            this.textboxWebserviceOptionLevel.visible = isPCMilerWebService;
            this.switchStreetLevel.visible = dropdownValue === pcMiler.onPrem;
            this.textboxWebserviceApiKey.visible = isPCMilerWebService;
            this.textboxMileMakerApiKey.visible = isMileMakerWeb;
            this.switchOrderHistoryRecords.visible = dropdownValue !== mileMaker.intelliroute;
            this.switchTollsCapable.visible = !isMileMakerWeb;
            this.textboxConnectionTimeout.visible = isMileMakerWeb || isPCMilerWebService || isProMiles;
            if (pcMiler.onPrem === dropdownValue) {
                await this.mainDataSource.search({"id": pcMiler.onPrem});
                this.setDistanceVersionsVisibility(false);
            } else if (pcMiler.web === dropdownValue) {
                await this.mainDataSource.search({"id": pcMiler.web});
                this.setDistanceVersionsVisibility(true);
                this.textboxWebserviceUrl.text = this.PCMILER_WEB_URL;
            } else if (mileMaker.web === dropdownValue) {
                await this.mainDataSource.search({"id": mileMaker.web})
                this.setDistanceVersionsVisibility(true);
            } else if (mileMaker.intelliroute === dropdownValue) {
                await this.mainDataSource.search({"id": mileMaker.intelliroute})
                this.setDistanceVersionsVisibility(false);
            }

            this.setQuickAddRowItems(selectedItem);
        }
    }

    private setQuickAddRowItems(selectedItem: DropdownItem) {
        const quickAddRow = this.tableDistanceProfiles.quickAddRow;
        if (quickAddRow) {
            const quickAddRowProfileId = quickAddRow.findComponentById("textboxId") as Textbox;
            const quickAddRowDescription = quickAddRow.findComponentById("textboxDescription") as Textbox;
            const quickAddLookupTypeTextbox = quickAddRow.findComponentById("textboxLookupType") as Textbox;
            const quickAddMethodTextbox = quickAddRow.findComponentById("textboxMethod") as Textbox;
            const quickAddVersionTextbox = quickAddRow.findComponentById("textboxVersion") as Textbox;
            const quickAddRowVersionCell = quickAddRow.cells[3];
            const versionCol = this.tableDistanceProfiles.getColumnByCaption("Version");

            if (selectedItem.value === MILEMAKER) {
                quickAddRowProfileId.text = null;
                quickAddRowDescription.text = null;
                quickAddLookupTypeTextbox.items = MILEMAKER_LOOKUP_TYPES;
                quickAddMethodTextbox.setSelectedItemFromDisplayValue(null);

                this.tableDistanceProfiles.setColumnVisibility(versionCol, true);
                quickAddRowVersionCell.visible = true;
                quickAddVersionTextbox.required = true;
            } else if (selectedItem.value === PCMILER_WEB) {
                quickAddRowProfileId.text = null;
                quickAddRowDescription.text = null;
                quickAddVersionTextbox.setSelectedItemFromDisplayValue(null);
                quickAddLookupTypeTextbox.setSelectedItemFromDisplayValue(null);
                quickAddLookupTypeTextbox.items = PCMILER_LOOKUP_TYPES;
                quickAddMethodTextbox.items = PCMILER_WEB_LOOKUP_METHODS;
                quickAddMethodTextbox.setSelectedItemFromDisplayValue(null);

                this.tableDistanceProfiles.setColumnVisibility(versionCol, true);
                quickAddRowVersionCell.visible = true;
                quickAddVersionTextbox.visible = true;
                quickAddVersionTextbox.required = true;
            } else if (selectedItem.value === PCMILER_ON_PREM) {
                quickAddRowProfileId.text = null;
                quickAddRowDescription.text = null;
                quickAddLookupTypeTextbox.items = PCMILER_LOOKUP_TYPES;
                quickAddMethodTextbox.items = PCMILER_ON_PREM_LOOKUP_METHODS;
                quickAddVersionTextbox.items = [];
                quickAddLookupTypeTextbox.setSelectedItemFromDisplayValue(null);
                quickAddMethodTextbox.setSelectedItemFromDisplayValue(null);

                this.tableDistanceProfiles.setColumnVisibility(versionCol, false);
                quickAddRowVersionCell.visible = false;
                quickAddVersionTextbox.required = false;
            } else if (selectedItem.value === INTELLIROUTE) {
                quickAddRowProfileId.text = null;
                quickAddRowDescription.text = null;
                quickAddLookupTypeTextbox.items = INTELLIROUTE_LOOKUP_TYPES;
                quickAddMethodTextbox.items = INTELLIROUTE_LOOKUP_METHODS;
                quickAddVersionTextbox.items = [];
                quickAddMethodTextbox.setSelectedItemFromDisplayValue(null);

                this.tableDistanceProfiles.setColumnVisibility(versionCol, false);
                quickAddRowVersionCell.visible = false;
                quickAddVersionTextbox.required = false;
            }

            this.setRowSourceValue(selectedItem);
        }
    }

    private async textboxIdOnBlur(event: BlurEvent) {
        const title = (event.target as Textbox);
        if (title.text.length === 0) {
            return;
        }

        await this.checkDuplicateProfileId(title);
    }

    private async checkDuplicateProfileId(textbox: Textbox) {
        const response = await Api.post("lme/dispatch/check-duplicate-code", { id: textbox.text, table: "distance_profile" });

        if (response?.data[0].is_duplicate) {
            Snackbar.showWarningSnackbar("The value " + textbox.text + " already exists in the table");
            textbox.text = null;
            textbox.setSelectedItemFromValue(null);
            textbox.focus();
        }
    }

    textboxVersionOnChange(event: ChangeEvent) {
        const serverTextbox = this.findComponentById("textboxDistanceVendorOptions") as Textbox;
        const server = serverTextbox.selectedItem;
        if (server.value === MILEMAKER) {
            const newValue = event.newValue;
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            const textboxMethod = tableRow.findComponentById("textboxMethod") as Textbox;

            if (textboxMethod) {
                if (newValue === "guide20") {
                    textboxMethod.items = MILEMAKER_GUIDE20_METHODS;
                } else if (newValue === "guide19") {
                    textboxMethod.items = MILEMAKER_GUIDE19_METHODS;
                }
            }
        }
    }

    // Listener event when lookup type texbox value changes
    textboxLookupTypeOnChange(event: ChangeEvent) {
        const newValue = event.newValue;
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        const textboxMethod = tableRow.findComponentById("textboxMethod") as Textbox;

        if (textboxMethod) {
            const isEnabled = newValue !== "Lat/Long";
            this.setMethodVisibilityAndRequirement(textboxMethod, isEnabled);
        }
    }

    tableDistanceProfilesOnRowModeChange(event: TableRowModeChangeEvent) {
        const tableRow = event.getTableRow();
        const textboxLookupType = tableRow.findComponentById("textboxLookupType") as Textbox;

        if(tableRow && textboxLookupType) {
            const isEnabled = textboxLookupType.text !== "Lat/Long";
            const textboxMethod = tableRow.findComponentById("textboxMethod") as Textbox;

            this.setMethodVisibilityAndRequirement(textboxMethod, isEnabled);
        }
    }

    setMethodVisibilityAndRequirement(textboxMethod: Textbox, isEnabled: boolean) {
        if(!isEnabled) {
            textboxMethod.setSelectedItemFromDisplayValue(null);
        }
        textboxMethod.enabled = isEnabled;
        textboxMethod.visible = isEnabled;
        textboxMethod.required = isEnabled;
        textboxMethod.requiredDuringAdd = isEnabled;
        textboxMethod.requiredDuringUpdate = isEnabled;
    }

    switchAdditionalUpdateCitySettingsOnChange() {
        this.panelUpdateCityFiles.visible = this.switchAdditionalUpdateCitySettings.checked;
    }

    async buttonUpdateCityFilesOnClick(event: ClickEvent) {
        const pcMilerVersion = this.textboxPcMilerUpdateVersion.text;
        const updateType = this.textboxUpdateType.selectedItem;
        const executeUpdates =  this.switchExecuteUpdates.checked;
        const printEditList = this.switchPrintEditList.checked;
        const updateLoacation = this.switchUpdateLocation.checked;
        const updateStops = this.switchUpdateStops.checked;
        this.validateUpdateCityFilesInput(pcMilerVersion, printEditList, executeUpdates, updateLoacation, updateStops);
        if (executeUpdates) {
            const title = "Confirm Changes";
            const dialogProps: Partial<YesNoDialogProps> = { noButtonCaption: "No", yesButtonCaption: "Yes" };
            const userChoice = await CommonDialogs.showYesNo("Proceed with executing updates?", title, dialogProps);
            if (userChoice === true) {
                // Make a Call to model and excutes updates
            }
        }
        if (updateLoacation) {
             // Make a Call to model and update location
        }
        if (updateStops) {
             // Make a Call to model and update stops
        }

        if (updateLoacation || updateStops) {
            const message = "Log file with results of updating the location IDs and the Stops with update type " + updateType.displayValue ;
            Snackbar.showSnackbar(message);
        }
    }

    validateUpdateCityFilesInput(pcMilerVersion: string, printEditList: boolean, executeUpdates: boolean, updateLocation: boolean, updateStops: boolean) {
        if (StringUtil.isEmptyString(pcMilerVersion) ){
            Snackbar.showWarningSnackbar("A PC*Miler version must be specified");
            return;
        }
        if (!printEditList && !executeUpdates && !updateLocation && !updateStops) {
            Snackbar.showWarningSnackbar("An option must be selected to perform updates to city files.");
            return;
        }
    }

    tableDistanceVersionsOnRowDisplay() {
        this.setTextboxVersions();
    }

    sourceDistanceVersionOnDisplay() {
    }

    tableDistanceProfilesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow !== undefined) {
            const serverTextbox = this.findComponentById("textboxDistanceVendorOptions") as Textbox;
            const textboxLookupType = tableRow.findComponentById("textboxLookupType") as Textbox;
            const textboxMethod = tableRow.findComponentById("textboxMethod") as Textbox;
            const textboxVersionId = tableRow.findComponentById("textboxVersion") as Textbox;
            const selectedItem = serverTextbox.selectedItem;

            // Filter textbox options if where applicable
            if (selectedItem) {
                const quickAddRow = this.tableDistanceProfiles.quickAddRow;

                if (selectedItem.value === MILEMAKER) {
                    if (quickAddRow) {
                        const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
                        quickAddVersionTextbox.items = this.versions;
                        this.setMileMakerLookupTypesBaseOnVersion();
                    }
                    textboxLookupType.items = MILEMAKER_LOOKUP_TYPES;
                    textboxVersionId.items = this.versions;
                    if (textboxVersionId.valueAsString === "guide20") {
                        textboxMethod.items = MILEMAKER_GUIDE20_METHODS;
                    } else if (textboxVersionId.valueAsString === "guide19") {
                        textboxMethod.items = MILEMAKER_GUIDE19_METHODS;
                    }
                } else if (selectedItem.value === PCMILER_WEB) {
                    if (quickAddRow) {
                        const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
                        quickAddVersionTextbox.items = this.versions;
                    }
                    textboxLookupType.items = PCMILER_LOOKUP_TYPES;
                    textboxMethod.items = PCMILER_WEB_LOOKUP_METHODS;
                    const col = this.tableDistanceProfiles.getColumnByCaption("Version");
                    this.tableDistanceProfiles.setColumnVisibility(col, true);
                    textboxVersionId.visible = true;
                    textboxVersionId.items = this.versions;
                } else if (selectedItem.value === PCMILER_ON_PREM) {
                    if (quickAddRow) {
                        const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
                        quickAddVersionTextbox.items = [];
                    }
                    textboxLookupType.items = PCMILER_LOOKUP_TYPES;
                    const col = this.tableDistanceProfiles.getColumnByCaption("Version");
                    this.tableDistanceProfiles.setColumnVisibility(col, false);
                    textboxMethod.items = PCMILER_ON_PREM_LOOKUP_METHODS;
                    textboxVersionId.visible = false;
                } else if (selectedItem.value === INTELLIROUTE) {
                    if (quickAddRow) {
                        const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
                        const quickAddLookupTypeTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxLookupType") as Textbox;
                        quickAddVersionTextbox.items = [];
                        quickAddLookupTypeTextbox.setSelectedItemFromValue(null);
                    }
                    textboxLookupType.items = INTELLIROUTE_LOOKUP_TYPES;
                    textboxMethod.items = INTELLIROUTE_LOOKUP_METHODS;
                }

                this.setRowSourceValue(selectedItem);
                this.setUpPreassignmentTooltipContent(tableRow);
            }
        }
        this.refreshPcMilerVersionControls();
    }

    setMileMakerLookupTypesBaseOnVersion() {
        const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
        const quickAddMethodTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxMethod") as Textbox;

        if (quickAddVersionTextbox.selectedItem && quickAddVersionTextbox.selectedItem.value === "guide20") {
            quickAddMethodTextbox.items = MILEMAKER_GUIDE20_METHODS;
        } else if (quickAddVersionTextbox.selectedItem && quickAddVersionTextbox.selectedItem.value === "guide19") {
            quickAddMethodTextbox.items = MILEMAKER_GUIDE19_METHODS;
        }
    }

    /**
     * Set the row's ServiceProvider before posting to the database
     *
     * @param source - Distance service provider name
     */
    private setRowSourceValue(source: DropdownItem) {
        const row: ModelRow = this.tableDistanceProfiles.quickAddData;
        if (row) {
            const serviceProvider: string = source.value;
            if (serviceProvider) {
                row.set("source", serviceProvider);
            }
        }
    }

    private hideServiceProvider() {
        const col = this.tableDistanceProfiles.getColumnByCaption("Service Provider");
        this.tableDistanceProfiles.setColumnVisibility(col, false);
    }

    showSlider(event: ClickEvent) {
        const sliderConfiguration = {
            [this.buttonDistancePreferences.id]: { sliderTitle: "Distance Preferences"},
        };
        const configuration = sliderConfiguration[event.target.id];
        if (configuration) {
            this.setupSingleControlLayout(configuration.sliderTitle);
        }
    }

    setupSingleControlLayout(title: string) {
        const layout = Layout.getLayout("lme/powerbroker/DistancePreferences") as DistancePreferences;
        if (layout == null) {
            Snackbar.showWarningSnackbar("Unable to Find the Layout for " + title);
            return;
        }
        const saveButton = SaveButton.create({
            id: "saveBtn",
            rowBreak: false,
            caption: "Save & Close",
            color: "McLeodWhite",
            backgroundColor: "primary",
            borderWidth: 0,
            onClick: async () => {
                    const response = await layout.postDataSources();
                    if (response) {
                        layout.slideOut();
                    }
            }
        });

        const editRowDecorator = new EditRowDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: 1250,
            overlayProps: {
                greyedBackground: true,
                closeHandler: async () => {
                    this.validateUnSaveChanges(layout)
                }
            },
            addlComponents: saveButton,
            onClose: () => {
                this.validateUnSaveChanges(layout)
            },
        });
    }

    async validateUnSaveChanges(layout: any) {
        if (!layout.mainDataSource.hasChanged()) {
            layout.slideOut();
            return;
        }

        const content = "There are unsaved changes. Are you sure you want to close this screen?";
        const title = "Unsaved changes";
        const dialogProps: Partial<YesNoDialogProps> = { noButtonCaption: "Discard Changes", yesButtonCaption: "Save Changes"};
        const userChoice = await CommonDialogs.showYesNo(content, title, dialogProps);
        if (userChoice === true) {
            const saveResponse = await layout.postDataSources();
            if (saveResponse) {
                layout.slideOut();
            }
        } else if (userChoice === false) {
            layout.slideOut();
        }
    }


    textboxEntryDelayOnBlur(event: BlurEvent) {
        const searchDelay = (event.target as Textbox).text;
        if (searchDelay !== "" && searchDelay !== null && searchDelay !== undefined) {
            const isNumeric = /^\d+$/.test(searchDelay);
            if (!isNumeric) {
                Snackbar.showWarningSnackbar("Only positive numeric values are allowed.");
                if (event.target.id === this.textboxTrimblePlacesEntryDelay.id) {
                    this.sourceDistanceSource?.activeRow?.set("trimble_places_entry_delay", 750)
                }
            }
        }
    }

    async switchEnableTrimblePlacesOnChange() {
        const isEnableTrimblePlaces = this.switchEnableTrimblePlaces.checked;
        this.setVisibility(this.switchEnableAddLocationTrimble, isEnableTrimblePlaces);
        this.setVisibility(this.switchEnableAddCustomerTrimble, isEnableTrimblePlaces);
        this.setVisibility(this.switchEnableStopLookupTrimble, isEnableTrimblePlaces);
        this.setVisibility(this.switchCreateLocationFromStopTrimble, isEnableTrimblePlaces);
        this.setVisibility(this.switchAllowDuplicateLocationCreationTrimble, isEnableTrimblePlaces);
        this.setVisibility(this.textboxTrimblePlacesEntryDelay, isEnableTrimblePlaces);
        if (this.sourceDistanceSource?.hasChanged()) {
            if (isEnableTrimblePlaces) {
                await this.validateTrimblePlaces();
            }
        }
    }

    setVisibility(component: Switch | Textbox, switchComponentCheck: boolean) {
        component.visible = switchComponentCheck;
        component.visibleDuringAdd = switchComponentCheck;
        component.visibleDuringUpdate = switchComponentCheck;
    }

    async validateTrimblePlaces() {
        const dispatchControlSource = this.sourceDispatchControl.activeRow;
        if (dispatchControlSource) {
            const googlePlacesEnabled: string = dispatchControlSource?.getString("enable_google_places");
            if ("Y" === googlePlacesEnabled) {
                const content = this.createContent("Google", "Trimble");
                const title = "Confirm Changes";
                const dialogProps: Partial<YesNoDialogProps> = {};
                const userChoice = await CommonDialogs.showYesNo(content, title, dialogProps);
                if (userChoice === true) {
                    this.setDispatchSourceActiveRow();
                    this.sourceDispatchControl.post();
                } else {
                    this.sourceDistanceSource.activeRow.set("enable_trimble_places", "N");
                }
            }
        }
    }

    setDispatchSourceActiveRow() {
        this.sourceDispatchControl.activeRow.set({enable_google_places: "N", enable_location_add_google: "N",enable_customer_add_google: "N",
            enable_stop_add_google: "N", create_location_from_stop: "N", enable_duplicate_add_google: "N", google_places_entry_delay: 750});
    }

    createContent(currentService: string, newService: string) {
        return `${currentService} Places is currently active. Only one places service can be active. Are you sure you want to set ${newService} Places as the active service?`;
    }

    setDistanceOptionsPanel() {
        this.tableDistanceProfiles.onEditLayoutLoaded = async (rowDecorator: EditRowDecorator, tableRow: TableRow) => {
            const slideout = rowDecorator.layout as DistanceOptions;

            rowDecorator.doAfterSlideOut = () => {
                this.mainDataSource.search({ id: this.sourceId });
            };

            rowDecorator.removeBtnCloseClickListener();
            rowDecorator.btnClose.addClickListener(() => this.validateUnSaveChanges(slideout));

            // When the user clicks outside of the slideout
            rowDecorator.overlayProps = {
                greyedBackground: true,
                closeHandler: async () => {
                    this.validateUnSaveChanges(slideout);
                }
            };

            const serverTextbox = this.findComponentById("textboxDistanceVendorOptions") as Textbox;
            const server = serverTextbox.selectedItem;
            const addNewProfile = rowDecorator.data._appending;

            rowDecorator.labelTitle.text = addNewProfile ? 'Create New Distance Profile' : `Edit Distance Profile`;

            const textboxVendor = rowDecorator.findComponentById("textboxVendor");
            textboxVendor.enabled = !addNewProfile;
            const textboxProfileId = rowDecorator.findComponentById("textboxProfileId") as Textbox;
            // Do not allow the Profile ID to be edited for an already existing Profile
            textboxProfileId.enabled = addNewProfile;
            if(addNewProfile && !StringUtil.isEmptyString(textboxProfileId.text)) {
                await this.checkDuplicateProfileId(textboxProfileId);
            }

            // Hide Save and Add Another Option button
            rowDecorator.multiButton.components[1].visible = false;

            rowDecorator.doAfterSlideIn = async () => {
                const pcMilerVersions = await this.getVersionIds(PCMILER_WEB);
                const mileMakerVersions = await this.getVersionIds(MILEMAKER);
                slideout.textboxPCMilerVersion.items = pcMilerVersions;
                slideout.textboxMMVersion.items = mileMakerVersions;
                slideout.setVendorSpecificOptions(server.value);
                const textboxVersion = server.value == PCMILER_WEB ? slideout.textboxPCMilerVersion : slideout.textboxMMVersion;
                textboxVersion.selectedItem = new DropdownItem(slideout.activeRow.get("version"));
                rowDecorator.validateSimple();
            }
        };
    }

    private setUpPreassignmentTooltipContent(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const labelCount = tableRow.findComponentById("labelCount");
        const profileOverrideCount = modelRow.data.count;
        if (profileOverrideCount > 0) {
            CommonTooltips.setTooltipContentLayout(
                labelCount, null, "lme/powerbroker/QuickInfoDistanceProfileOverride", null,
                {"profile_id": modelRow.get("id")}
            );
            labelCount.tooltipDynamicOptions = { position: Alignment.LEFT };
        }
    }

    //* This is an event handler for versio id dropdown selection */
    textboxVersionIdAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.selection) {
            this.checkDuplicateVersionId(event.selection.value);
        }
    }

    private async checkDuplicateVersionId(version_id: string) {
        const response = await Api.post("lme/dispatch/check-duplicate-code",
            { id: version_id,
              table: "distance_version",
              alt_field_name_for_dup_check: "version_id",
              composite_key_values: { source_id: this.sourceId }
            });

        if (response?.data[0].is_duplicate) {
            Snackbar.showWarningSnackbar("The version '" + version_id + "' already exists for this distance source. Please choose a different version.");
            const quickAddVersionTextbox = this.tableDistanceVersions.quickAddRow.findComponentById("textboxVersionId") as Textbox;
            quickAddVersionTextbox.text = null;
            quickAddVersionTextbox.setSelectedItemFromValue(null);
            quickAddVersionTextbox.focus();
        }
    }

    // Listener for Distance Version toggle
    async switchDistanceVersionsOnChange() {
        const showDistanceVersions = this.switchDistanceVersions.checked;
        this.panelDistanceVersions.visible = showDistanceVersions;

        if (showDistanceVersions) {
            this.sourceDistanceVersion.orderBy = "version_id desc";
            await this.sourceDistanceVersion.search({source: this.sourceId});
        }
    }

    /** This is an event handler for the onContentsChanged event of tableDistanceVersions.  */
    tableDistanceVersionsOnContentsChanged(event: TableContentsChangedEvent) {
        const serverTextbox = this.findComponentById("textboxDistanceVendorOptions") as Textbox;
        const server = serverTextbox.selectedItem.value;
        this.getAvailableDistanceSourceVersions(server);
    }

    private updateTableDistanceProfileQuickAddVersionItems() {
        const quickAddRow = this.tableDistanceProfiles.quickAddRow;
        if (quickAddRow) {
            const quickAddVersionTextbox = this.tableDistanceProfiles.quickAddRow.findComponentById("textboxVersion") as Textbox;
            quickAddVersionTextbox.items = this.versions;
        }
    }

    private updateTableDistanceProfileVersionItems() {
        this.tableDistanceProfiles.allRows.forEach(tableRow => {
            const textboxVersionId = tableRow.findComponentById("textboxVersion") as Textbox;
            if (textboxVersionId) {
                textboxVersionId.items = this.versions;
            }
        });
    }

    private setTextboxVersions() {
        const quickAddRow = this.tableDistanceVersions.quickAddRow;
        const textboxVersion = quickAddRow?.findComponentById("textboxVersionId") as Textbox;
        const serverTextbox = this.findComponentById("textboxDistanceVendorOptions") as Textbox;
        if (quickAddRow && textboxVersion && serverTextbox) {
            const server = serverTextbox?.selectedItem?.value;
            if(server) {
                if (server === PCMILER_WEB) {
                    textboxVersion.items = this.getPCMilerVersions();
                    this.setTableDistanceVersionSource(PCMILER_WEB);
                } else if (server === MILEMAKER) {
                    textboxVersion.items = this.getMileMakerVersions();
                    this.setTableDistanceVersionSource(MILEMAKER);
                }
            }
        }
    }

    private getMileMakerVersions(): DropdownItem[] {
        const versions = [];
        versions.push(new DropdownItem("guide19", "guide19"));
        versions.push(new DropdownItem("guide20", "guide20"));
        return versions;
    }

    private getPCMilerVersions(): DropdownItem[] {
        const versions = [];
        versions.push(new DropdownItem("CURRENT", "CURRENT"));
        for (let i = LATEST_PCMILER_VERSION; i >= OLDEST_PCMILER_VERSION; i--) {
            versions.push(new DropdownItem(i.toString(), i.toString()))
        }
        return versions;
    }

    setTableDistanceVersionSource(selectedItem: string) {
        const quickAddRow = this.tableDistanceVersions.quickAddRow;
        const textboxSource = quickAddRow?.findComponentById("textboxSourceId") as Textbox;

        if (quickAddRow && textboxSource) {
            textboxSource.setSelectedItemFromValue(selectedItem);
        }
    }

    private setDistanceVersionsVisibility(isVisible: boolean) {
        this.labelDistanceVersions.visible = isVisible;
        this.switchDistanceVersions.visible = isVisible;
        this.switchDistanceVersions.checked = false;
        // If hiding, also ensure the panel itself is hidden if it was tied to the switch state
        if (!isVisible) {
            this.panelDistanceVersions.visible = false;
        }
    }

    private refreshPcMilerVersionControls() {
        // Only proceed if the current source is PCMILER_WEB and table and relevant textboxes exist
        if (this.sourceId !== pcMiler.web || !this.tableDistanceProfiles || !this.textboxWebserviceOptionLevel) {
            return;
        }

        const selectedOptionLevel = this.textboxWebserviceOptionLevel.valueAsString;
        const isPcMilerPremium = selectedOptionLevel === "P";

        // Update the quick add row
        const quickAddRow = this.tableDistanceProfiles.quickAddRow;
        if (quickAddRow) {
            const quickAddVersionTextbox = quickAddRow.findComponentById("textboxVersion") as Textbox;
            if (quickAddVersionTextbox) {
                quickAddVersionTextbox.enabled = isPcMilerPremium;
                quickAddVersionTextbox.required = isPcMilerPremium;
                if (!isPcMilerPremium) {
                    quickAddVersionTextbox.setSelectedItemFromDisplayValue(null);
                }
            }
        }

        // Update all existing rows
        this.tableDistanceProfiles.allRows.forEach(tableRow => {
            const textboxVersion = tableRow.findComponentById("textboxVersion") as Textbox;
            if (textboxVersion) {
                textboxVersion.enabled = isPcMilerPremium;
                textboxVersion.required = isPcMilerPremium;
            }
        });

        // Update switchDistanceVersions enabled and visible state
        if (this.switchDistanceVersions) {
            this.switchDistanceVersions.enabled = isPcMilerPremium;
            this.switchDistanceVersions.visible = isPcMilerPremium;
            this.labelDistanceVersions.visible = isPcMilerPremium;

            if (!isPcMilerPremium) {
                this.switchDistanceVersions.checked = false;
                // Ensure the panel is hidden if the switch is disabled and unchecked
                this.panelDistanceVersions.visible = false;
            }
        }

        // Ensure the Version column is visible for PCMILER_WEB
        const versionCol = this.tableDistanceProfiles.getColumnByCaption("Version");
        if (versionCol) {
            this.tableDistanceProfiles.setColumnVisibility(versionCol, true);
        }
    }

    async textboxWebserviceOptionLevelOnChange(event: ChangeEvent) {
        const selectedOptionLevel = this.textboxWebserviceOptionLevel.valueAsString;
        const isPcMilerPremium = selectedOptionLevel === "P";

        // Show/hide switchDistanceVersions based on option level
        this.switchDistanceVersions.visible = isPcMilerPremium;
        this.labelDistanceVersions.visible = isPcMilerPremium;

        // If not premium, ensure the switch is unchecked and panel is hidden
        if (!isPcMilerPremium) {
            this.switchDistanceVersions.checked = false;
            this.panelDistanceVersions.visible = false;
        }

        this.refreshPcMilerVersionControls();
    }
}
